import React from "react";
import ActionButtons from "./ActionButtons";
import AnimatedTextWithCursor from "./AnimatedTextWithCursor";
import { FlowChoice, MessageType } from "@/types";
import { useTextWithLinks } from "../hooks/useTextWithLinks";

/**
 * メッセージバブルのプロパティ
 */
interface MessageBubbleProps extends MessageType {
  onLike: () => void;
  onDislike: () => void;
  isThinking?: boolean;
  botName?: string;
  onFlowChoice?: (choice: FlowChoice) => void;
}

/**
 * メッセージバブル
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({
  sent,
  text,
  timestamp,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  avatar: _avatar, // ユーザーのアイコン、まだ使用しないけど
  isRobot = false,
  isThinking,
  isAnswering,
  onLike,
  onDislike,
  feedback,
  shouldDisplayFeedback,
  referLinks,
  botName = "パスビーさん",
  isFlow,
  flowData,
  isChoiceDisabled,
  onFlowChoice,
}) => {
  const { processTextWithLinks } = useTextWithLinks();

  // フロー会話終了時は何も表示しない
  if (flowData?.flowEnded) {
    return null;
  }

  // レンダリング
  const renderContent = () => {
    if (isRobot && !isThinking) {
      if (isAnswering) {
        return <AnimatedTextWithCursor text={text} />;
      } else if (typeof text === "string") {
        return processTextWithLinks(text);
      }
    }
    return text;
  };

  return (
    <div
      className={`flex flex-col mb-6 ${sent ? "items-end" : "items-start"}`}
      style={{
        marginLeft: "0",
        marginRight: "0",
        marginTop: sent ? "5px" : "10px",
        marginBottom: sent ? "10px" : "5px",
      }}
    >
      {(isRobot || isThinking) && (
        <div
          className={`flex w-full items-start ${
            sent ? "flex-row-reverse" : "flex-row"
          }`}
        >
          <div className="w-8 h-8 bg-transparent rounded-full flex items-center justify-center">
            <div
              className="w-8 h-8 bg-no-repeat bg-center bg-contain"
              style={{
                backgroundImage: `var(--img-bot-avatar)`,
              }}
              role="img"
              aria-label="ボットアバター"
            />
          </div>
          <div
            className="text-sm pt-1.5 ml-2"
            style={{ color: "var(--main-text)" }}
          >
            {botName}
          </div>
        </div>
      )}

      <div
        className={`flex w-full ${
          isRobot ? "flex-row-reverse" : "flex-row"
        } items-end justify-end ${
          isRobot || isThinking ? "ml-0" : "ml-10"
        } gap-2`}
      >
        <div className="self-end shrink-0">
          <div
            className={`opacity-70 text-xs ${isRobot ? "mx-1" : "mx-0"}`}
            style={{ color: "var(--main-text)" }}
          >
            {timestamp}
          </div>
        </div>

        <div
          className={`
            ${sent ? "pathbee-user-bubble" : "pathbee-bot-bubble"}
            ${isThinking ? "border-0 w-36" : ""}
            ${!isRobot ? "ml-1" : "ml-5"}
            whitespace-pre-wrap break-words
            shrink-0
          `}
          style={{
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            lineHeight: "1.6",
            whiteSpace: "pre-wrap",
          }}
        >
          {renderContent()}
          {referLinks && referLinks.length > 0 && (
            <div
              style={{
                marginTop: "25px",
                marginBottom: "16px",
                display: "block",
              }}
            >
              {referLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    block underline text-sm hover:opacity-80
                    ${index > 0 ? "mt-1" : ""}
                  `}
                  style={{ color: "var(--bot-bubble-link, #1565c0)" }}
                >
                  {link.title}
                </a>
              ))}
            </div>
          )}
          {isRobot && isFlow && flowData && flowData.linkButtons.length > 0 && (
            <div className="flex flex-col gap-2 mt-3">
              {flowData.linkButtons.map((lb) => (
                <a
                  key={lb.id}
                  href={lb.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-1 rounded-full text-xs transition no-underline inline-flex items-center justify-center gap-1 self-start"
                  style={{
                    border: "1px solid var(--flow-choice-border)",
                    backgroundColor: "var(--flow-choice-bg)",
                    color: "var(--flow-choice-text)",
                    fontSize: "14px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--flow-choice-bg-hover)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--flow-choice-bg)";
                  }}
                >
                  {lb.label || "\u00A0"}
                  <span aria-hidden="true">↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {isRobot &&
        isFlow &&
        flowData &&
        flowData.choices.length > 0 &&
        !isAnswering && (
          <div
            className="flex flex-col gap-2 self-end items-end max-w-[85%]"
            style={{ marginTop: "8px", marginBottom: "8px" }}
          >
            {flowData.choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                disabled={isChoiceDisabled || !onFlowChoice}
                onClick={() => onFlowChoice && onFlowChoice(choice)}
                className="px-4 py-1 rounded-full text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  border: "1px solid var(--flow-choice-border)",
                  backgroundColor: "var(--flow-choice-bg)",
                  color: "var(--flow-choice-text)",
                  fontSize: "14px",
                }}
                onMouseOver={(e) => {
                  if (!isChoiceDisabled && onFlowChoice) {
                    e.currentTarget.style.backgroundColor =
                      "var(--flow-choice-bg-hover)";
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "var(--flow-choice-bg)";
                }}
              >
                {choice.label || "\u00A0"}
              </button>
            ))}
          </div>
        )}

      {shouldDisplayFeedback &&
        isRobot &&
        !isAnswering &&
        onLike &&
        onDislike && (
          <div
            className="flex flex-col items-start ml-10"
            style={{ marginTop: "5px", marginBottom: "16px" }}
          >
            <div className="flex gap-2">
              <ActionButtons
                onLike={onLike}
                onDislike={onDislike}
                feedback={feedback}
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default MessageBubble;
